import { useEffect, useMemo, useState } from 'react';

import { Check, DeleteOutline, Restore } from '@mui/icons-material';
import {
  Alert,
  Autocomplete,
  Button,
  Chip,
  MenuItem,
  Select,
  Snackbar,
  TextField,
  Typography
} from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { useQuery } from '@tanstack/react-query';
import dayjs, { Dayjs } from 'dayjs';
import { useFormik } from 'formik';
import moment from 'moment';

import Container from 'components/common/Container';
import TextInput from 'components/common/TextInput';
import api from 'utils/api';
import { useConstants } from 'utils/hooks';
import { FetchedEvent } from './types';
import { FormSensorType, useAddEventModal } from './utils';
import { useFetchEventTypes, type EventType } from '../tables/state';

const EVENTS_API = '/admin/events';

interface FormValues {
  buoyId: number;
  eventType?: EventType;
  comments?: string;
  loggedBy: string;
}

// =============================================================================
export default function EditEventTableModal(props: {
  rowData: FetchedEvent;
  handleRefreshTable: () => void;
  handleCloseEditModal: () => void;
}) {
  const { CONSTANTS } = useConstants();
  const [snackbar, setSnackbar] = useState('');
  const [startTime, setStartTime] = useState<Dayjs | null>(
    dayjs(Number(props.rowData.startTs))
  );
  const [endTime, setEndTime] = useState<Dayjs | null>(
    dayjs(Number(props.rowData.endTs))
  );
  const { handleRefreshTable } = props;
  const [triggerUpdate, setTriggerUpdate] = useState(false);

  const [selectedSensors, setSelectedSensors] = useState<Array<FormSensorType>>(
    []
  );

  const { eventTypes } = useFetchEventTypes();

  const formik: any = useFormik<FormValues>({
    //Set initial values to props data so users can just edit as per needed.
    initialValues: {
      buoyId: props.rowData.buoyId,
      eventType: eventTypes.find(
        (eventType) => eventType.id == props.rowData.eventTypeId
      ),
      loggedBy: props.rowData.createdByName,
      comments: props.rowData.comment
    },
    onSubmit: async (values) => {
      if (!values.buoyId || !values.eventType) {
        setSnackbar('Fields cannot be empty!');
        return;
      }
      if (!startTime || !endTime) {
        setSnackbar('Invalid start or end time');
        return;
      }
      if (startTime && endTime && endTime.diff(startTime) < 0) {
        setSnackbar('End time must be later than start time.');
        return;
      }
      const postValues = convert(values);
      try {
        await api.patch(EVENTS_API + '/' + props.rowData.id, postValues, {
          headers: {
            'Content-Type': 'application/json'
          }
        });
        handleRefreshTable();
        handleClose();
      } catch (err: any) {
        setSnackbar('Server error, please try again later!');
      }
    }
  });

  const {
    buoys: locations,
    isGetBuoysPending: isLocationsPending,
    sensors
  } = useAddEventModal({
    setSnackbar,
    selectedBuoyId: formik.values.buoyId,
    setSelectedSensors,
    triggerUpdate
  });

  const { data: _eventSensors } = useQuery<
    Array<{
      sensorClass: string;
      ownerSensorId: number;
      ownerSensorClassId: number;
      depth: string;
      parameter: string;
      id: number;
    }>
  >({
    queryKey: ['event-details', props.rowData.id],
    queryFn: () =>
      api
        .get(
          `/admin/eventDetails?${new URLSearchParams({ eventId: props.rowData.id.toString() }).toString()}`
        )
        .then((res) => res.data),
    staleTime: 0,
    enabled: !isLocationsPending // ensure this triggers state update after initial location update
  });

  const eventSensors = useMemo(() => {
    const paramDepthsGroupedBySensor: Record<
      string,
      {
        details: {
          ownerSensorId: number;
          ownerSensorClassId: number;
        };
        params: Record<string, Set<string>>;
      }
    > = {};
    (_eventSensors ?? []).forEach((sensor) => {
      if (!paramDepthsGroupedBySensor[sensor.sensorClass]) {
        paramDepthsGroupedBySensor[sensor.sensorClass] = {
          details: {
            ownerSensorClassId: sensor.ownerSensorClassId,
            ownerSensorId: sensor.ownerSensorId
          },
          params: {}
        };
      }
      if (
        !paramDepthsGroupedBySensor[sensor.sensorClass].params[sensor.parameter]
      ) {
        paramDepthsGroupedBySensor[sensor.sensorClass].params[
          sensor.parameter
        ] = new Set();
      }
      paramDepthsGroupedBySensor[sensor.sensorClass].params[
        sensor.parameter
      ].add(sensor.depth);
    });
    return Object.entries(paramDepthsGroupedBySensor);
  }, [_eventSensors]);

  // =============================================================================
  /*Converts form values into values stored in Events API 
  Parameters:
  values: Stored Formik field values
  length: maxId in events
  */
  function convert(values: FormValues): {
    comment: string;
    endTs: Dayjs;
    buoyId: number;
    startTs: Dayjs;
    sensors: Array<any>;
    eventTypeId: string;
    id: string;
  } {
    const output = {
      comment: values.comments,
      endTs: convertTime(endTime),
      buoyId: values.buoyId,
      eventTypeId: values.eventType!.id,
      startTs: convertTime(startTime),
      sensors: selectedSensors,
      id: props.rowData.id
    };
    return JSON.parse(JSON.stringify(output));
  }

  function convertTime(dateTime: Dayjs | null) {
    const dateTimeStr = dateTime?.toString();
    const formattedDateTime = moment(dateTimeStr).valueOf();
    return formattedDateTime;
  }

  const handleClose = () => {
    props.handleCloseEditModal();
  };

  const modalContainerStyle = {
    marginBottom: 48,
    marginTop: 48,
    border: '2px solid black',
    borderRadius: '20px',
    backgroundColor: 'white'
  };

  useEffect(() => {
    if (formik.values.buoyId !== props.rowData.buoyId) {
      setTriggerUpdate(true);
    }
  }, [formik.values.buoyId]);

  useEffect(() => {
    setSelectedSensors(
      Object.values(eventSensors).map(([_s, v]) => ({
        ownerSensorClassId: Number.parseInt(
          v.details.ownerSensorClassId.toString()
        ),
        ownerSensorId: Number.parseInt(v.details.ownerSensorId.toString())
      }))
    );
  }, [eventSensors]);

  // =============================================================================
  return (
    <div
      style={{
        maxWidth: '100%',
        alignContent: 'center',
        border: 20,
        margin: 'auto'
      }}
    >
      <Container style={modalContainerStyle}>
        <div style={{ height: 40 }} />
        <Typography variant='h2' color='blue.900' align='center'>
          Update Event
        </Typography>
        <form
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '40px 0'
          }}
        >
          <>
            <div
              style={{
                marginBottom: 32,
                width: '100%',
                maxWidth: 400
              }}
            >
              <DateTimePicker
                className='date-time-picker'
                renderInput={(props) => <TextField {...props} />}
                label='Start Time'
                value={startTime}
                onChange={(newValue: any) => {
                  if (!newValue.isValid()) {
                    setStartTime(null);
                    return;
                  }
                  setStartTime(newValue);
                }}
              />
            </div>
            <div
              style={{
                marginBottom: 32,
                width: '100%',
                maxWidth: 400
              }}
            >
              <DateTimePicker
                className='date-time-picker'
                renderInput={(props) => <TextField {...props} />}
                label='End Time'
                value={endTime}
                onChange={(newValue: any) => {
                  if (!newValue.isValid()) {
                    setEndTime(null);
                    return;
                  }
                  setEndTime(newValue);
                }}
              />
            </div>
            <div
              style={{
                width: '100%',
                maxWidth: 400,
                marginBottom: 16,
                display: 'flex',
                flexDirection: 'column',
                gap: 6
              }}
            >
              <span
                style={{
                  fontWeight: 'bold'
                }}
              >
                Location
              </span>
              <Select
                variant='outlined'
                id='buoyId'
                name='buoyId'
                placeholder=''
                value={formik.values.buoyId}
                onChange={formik.handleChange}
                error={formik.touched.buoyId && Boolean(formik.errors.buoyId)}
              >
                {locations.map(({ id, locationFF }) => (
                  <MenuItem key={id} value={id}>
                    {locationFF}
                  </MenuItem>
                ))}
              </Select>
            </div>
            <div
              style={{
                width: '100%',
                maxWidth: '400px',
                marginBottom: 16,
                display: 'flex',
                flexDirection: 'column',
                gap: '10px'
              }}
            >
              <div
                style={{
                  width: '100%',
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: '20px'
                }}
              >
                <span
                  style={{
                    fontWeight: 'bold'
                  }}
                >
                  Sensors
                </span>
                <Chip
                  label='Select all'
                  disabled={
                    !formik.values.buoyId ||
                    selectedSensors.length === (sensors ?? []).length
                  }
                  onClick={(e) => {
                    e.preventDefault();
                    if (sensors) {
                      setSelectedSensors(
                        sensors.map(
                          ({ ownerSensorClassId, ownerSensorId }) => ({
                            ownerSensorClassId,
                            ownerSensorId
                          })
                        )
                      );
                    }
                  }}
                />
                <Chip
                  label='Remove all'
                  icon={<DeleteOutline style={{ height: 16, width: 16 }} />}
                  style={{
                    paddingLeft: '3px'
                  }}
                  onClick={(e) => {
                    e.preventDefault();
                    setSelectedSensors([]);
                  }}
                />
                <Chip
                  label='Reset'
                  icon={<Restore style={{ height: 16, width: 16 }} />}
                  style={{
                    paddingLeft: '3px'
                  }}
                  onClick={(e) => {
                    e.preventDefault();
                    formik.setFieldValue('buoyId', props.rowData.buoyId);
                    setTriggerUpdate(false);
                    setSelectedSensors(
                      Object.values(eventSensors).map(([_s, v]) => ({
                        ownerSensorClassId: Number.parseInt(
                          v.details.ownerSensorClassId.toString()
                        ),
                        ownerSensorId: Number.parseInt(
                          v.details.ownerSensorId.toString()
                        )
                      }))
                    );
                  }}
                />
              </div>
              <div
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: '12px'
                }}
              >
                {sensors?.map((sensor, idx) => {
                  const isSelected =
                    selectedSensors.findIndex(
                      (v) =>
                        v.ownerSensorClassId === sensor.ownerSensorClassId &&
                        v.ownerSensorId === sensor.ownerSensorId
                    ) > -1;
                  return (
                    <Chip
                      onClick={(e) => {
                        e.preventDefault();
                        if (isSelected) {
                          setSelectedSensors((ssrs) =>
                            ssrs.filter(
                              (s) =>
                                s.ownerSensorClassId !==
                                  sensor.ownerSensorClassId &&
                                s.ownerSensorId !== sensor.ownerSensorId
                            )
                          );
                        } else {
                          setSelectedSensors((ssrs) => [
                            ...ssrs,
                            {
                              ownerSensorClassId: sensor.ownerSensorClassId,
                              ownerSensorId: sensor.ownerSensorId
                            }
                          ]);
                        }
                      }}
                      icon={
                        isSelected ? (
                          <Check
                            style={{
                              height: '16px',
                              width: '16px'
                            }}
                          />
                        ) : undefined
                      }
                      variant={isSelected ? 'filled' : 'outlined'}
                      label={sensor.sensorClass}
                      key={idx}
                    />
                  );
                })}
                {formik.values.buoyId !== '' &&
                  (!sensors || sensors.length === 0) && (
                    <div>
                      <span>
                        No sensors are available for this location yet.
                      </span>
                    </div>
                  )}
              </div>
            </div>

            <Autocomplete
              options={eventTypes}
              value={formik.values.eventType}
              onChange={(_, newValue) =>
                formik.setFieldValue('eventType', newValue)
              }
              getOptionLabel={(option) => option.eventType}
              sx={{
                width: '100%',
                maxWidth: 400,
                display: 'flex',
                flexDirection: 'column'
              }}
              renderInput={(params) => {
                return (
                  <TextInput
                    textFieldProps={params}
                    variant='outlined'
                    id='event-type'
                    name='eventType'
                    label='Event type'
                    placeholder=''
                    error={
                      formik.touched.eventType &&
                      Boolean(formik.errors.eventType)
                    }
                    helperText={
                      formik.touched.eventType && formik.errors.eventType
                    }
                    labelStyle={{ marginLeft: 0, marginBottom: 6 }}
                    containerStyle={{
                      width: '100%',
                      maxWidth: 400,
                      display: 'flex',
                      flexDirection: 'column'
                    }}
                    sx={{
                      borderRadius: 4,
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 4
                      }
                    }}
                  />
                );
              }}
            />

            <TextInput
              variant='outlined'
              id='loggedBy'
              name='loggedBy'
              label='Logged By'
              placeholder=''
              disabled={true}
              value={formik.values.loggedBy}
              onChange={formik.handleChange}
              error={formik.touched.loggedBy && Boolean(formik.errors.loggedBy)}
              helperText={formik.touched.loggedBy && formik.errors.loggedBy}
              labelStyle={{ marginLeft: 0, marginBottom: 6 }}
              containerStyle={{
                width: '100%',
                maxWidth: 400,
                display: 'flex',
                flexDirection: 'column'
              }}
              sx={{
                borderRadius: 4,
                '& .MuiOutlinedInput-root': {
                  borderRadius: 4
                }
              }}
            />
            <TextField
              variant='outlined'
              id='comments'
              name='comments'
              label='Additional Comments'
              placeholder='Optional'
              value={formik.values.comments}
              onChange={formik.handleChange}
              error={formik.touched.comments && Boolean(formik.errors.comments)}
              helperText={formik.touched.comments && formik.errors.comments}
              style={{
                width: '100%',
                maxWidth: 400,
                marginBottom: 32
              }}
              multiline
              rows={6}
            />
            {/* <div style={{ height: 16 }} /> */}
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <Button
                type='button'
                variant='secondary'
                color='dark'
                onClick={handleClose}
                style={{ marginRight: 10 }}
              >
                Close
              </Button>
              <Button
                type='button'
                variant='primary'
                color='dark'
                onClick={() => formik.handleSubmit(formik.values)}
              >
                Save Changes
              </Button>
            </div>
            <div style={{ height: 40 }} />
          </>
        </form>
      </Container>
      <Snackbar
        open={!!snackbar}
        autoHideDuration={3000}
        onClose={() => setSnackbar('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          sx={{
            width: '100%',
            maxWidth: 300
          }}
          onClose={() => setSnackbar('')}
          variant={'filled'}
          severity={'error'}
        >
          {snackbar}
        </Alert>
      </Snackbar>
    </div>
  );
}
